import { useModal } from '@billy/design-system'
import { useCallback, useMemo } from 'react'
import { FieldErrors, UseFormClearErrors, UseFormGetValues } from 'react-hook-form'

import { ImportedInvoiceExtractionParty } from '@features/importedInvoices/types/importedInvoiceExtraction'
import { ImportedInvoiceReviewFormValues } from '@features/importedInvoices/types/importedInvoiceReviewFormSchema'

import { ModalId } from '../../../../../../../enums/modalId'
import { getImportedInvoiceCustomerFormValues } from '../utils/getImportedInvoiceCustomerFormValues'

interface UseImportedInvoiceReviewClientCreationOptions {
  clearErrors: UseFormClearErrors<ImportedInvoiceReviewFormValues>
  client?: ImportedInvoiceExtractionParty | null
  getValues: UseFormGetValues<ImportedInvoiceReviewFormValues>
  isClientToCreate: boolean
}

/**
 * A new client the customer search did not find is created on approve. While the form still holds
 * only its extracted name, and that is all that keeps the invoice from being approved, Approve opens
 * the customer modal prefilled from the extraction instead of asking the reader to pick a client.
 * A client the reader cleared, or the "neither" case, still has to be picked.
 */
export const useImportedInvoiceReviewClientCreation = ({
  clearErrors,
  client,
  getValues,
  isClientToCreate,
}: UseImportedInvoiceReviewClientCreationOptions) => {
  const { open: openCustomerModal } = useModal(ModalId.CustomerModal)
  const { customerType, initialValues } = useMemo(() => getImportedInvoiceCustomerFormValues(client), [client])

  const handleInvalidSubmit = useCallback(
    (errors: FieldErrors<ImportedInvoiceReviewFormValues>) => {
      const formClient = getValues('client')
      const isOnlyClientInvalid = Object.keys(errors).every((field) => field === 'client')

      if (!isClientToCreate || !isOnlyClientInvalid || !formClient || formClient.id) {
        return
      }

      clearErrors('client')
      openCustomerModal()
    },
    [clearErrors, getValues, isClientToCreate, openCustomerModal],
  )

  return { customerType, handleInvalidSubmit, initialValues }
}
